/**
 * How the My Uniforms page reads a flat issuance list.
 *
 * Both rules here restate a backend one on purpose, because the employee's own
 * page disagreeing with the report that chases the same garment is worse than
 * the duplication:
 *
 *  - the buckets follow `CURRENT_ISSUANCE_STATUSES` in `uniform/replacement.py`
 *    (ISSUED + ACCEPTED are what an employee holds), split so that the ISSUED
 *    half — the only status accept/reject will touch — is the half we ask about.
 *  - the replacement pill follows `line_status()` in that same module, including
 *    its 30-day `DEFAULT_DUE_WITHIN_DAYS` window and its `<=` boundary.
 */

// Terminal: the garment is no longer part of what the employee is wearing.
// Anything NOT listed here — PENDING today, whatever is added next — stays in
// `current`, so a status this file has not caught up with degrades to "shown
// with its own label" instead of disappearing out of someone's list.
const PAST_STATUSES = new Set(['REJECTED', 'RETURNED', 'SUPERSEDED']);

// Mirrors DEFAULT_DUE_WITHIN_DAYS in uniform/replacement.py.
export const DUE_WITHIN_DAYS = 30;

export const groupIssuances = (rows) => {
  const groups = { pending: [], current: [], past: [] };
  for (const row of rows || []) {
    if (row?.status === 'ISSUED') groups.pending.push(row);
    else if (PAST_STATUSES.has(row?.status)) groups.past.push(row);
    else groups.current.push(row);
  }
  return groups;
};

// Calendar-day number for a 'YYYY-MM-DD' string. Built through Date.UTC so the
// value is the date the operator typed, never that date shifted into the
// viewer's timezone — a garment due today must not read as overdue at 11pm.
const dayNumberFromString = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  if (!match) return null;
  const [, year, month, day] = match;
  return Date.UTC(Number(year), Number(month) - 1, Number(day)) / 86400000;
};

// The same day number for a Date, read in the viewer's own timezone: "today" is
// the date on the wall behind them, not the UTC instant.
const dayNumberFromDate = (date) =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000;

export const daysUntilReplacement = (nextReplacementDate, today = new Date()) => {
  const due = dayNumberFromString(nextReplacementDate);
  if (due === null) return null;
  return due - dayNumberFromDate(today);
};

export const replacementStatus = (nextReplacementDate, today = new Date()) => {
  const days = daysUntilReplacement(nextReplacementDate, today);
  if (days === null) return 'ok';
  if (days < 0) return 'overdue';
  return days <= DUE_WITHIN_DAYS ? 'due_soon' : 'ok';
};
